import React, { useEffect, useState, useRef } from "react";
import { Loader2, MessageSquare, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { FetchAllAdminInquiries, ClearAdminInquiryAction } from "../../Store/Actions/AdminInquiryActions";

const STATUS_TABS = [
  { label: "All", value: undefined },
  { label: "Open", value: "OPEN" },
  { label: "In Progress", value: "IN_PROGRESS" },
  { label: "Closed", value: "CLOSED" },
];

const STATUS_STYLES = {
  OPEN: "bg-green-100 text-green-700",
  IN_PROGRESS: "bg-yellow-100 text-yellow-700",
  CLOSED: "bg-gray-100 text-gray-600",
};

const STATUS_LABELS = {
  OPEN: "Open",
  IN_PROGRESS: "In Progress",
  CLOSED: "Closed",
};

const Spinner = () => (
  <div className="flex justify-center items-center py-24">
    <Loader2 className="animate-spin text-themeColor" size={32} />
  </div>
);

function formatDate(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const MainAdminInquiries = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { inquiries, loader, actionError } = useSelector((state) => state.adminInquiry);
  const [activeTab, setActiveTab] = useState(undefined);
  const errorHandled = useRef(false);

  useEffect(() => {
    errorHandled.current = false;
    dispatch(FetchAllAdminInquiries(activeTab));
  }, [dispatch, activeTab]);

  useEffect(() => {
    if (actionError && !errorHandled.current) {
      errorHandled.current = true;
      toast.error(actionError.message || "An error occurred.");
      dispatch(ClearAdminInquiryAction());
    }
  }, [actionError, dispatch]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-popinsBold">Admin Inquiries</h1>
        <p className="text-sm font-popinsRegular text-[#61758A] mt-1">
          View and respond to inquiries submitted by nonprofit users.
        </p>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.label}
            onClick={() => setActiveTab(tab.value)}
            className={`px-4 py-1.5 rounded-full text-sm font-popinsMedium transition border ${
              activeTab === tab.value
                ? "bg-themeColor text-white border-themeColor"
                : "bg-white text-gray-600 border-gray-200 hover:border-themeColor/50"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      {loader ? (
        <Spinner />
      ) : inquiries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <MessageSquare size={48} className="mb-4 opacity-40" />
          <p className="text-base font-popinsMedium">No inquiries found</p>
          <p className="text-sm font-popinsRegular mt-1">
            Inquiries from nonprofit users will appear here.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide">
                    Subject
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide hidden md:table-cell">
                    Nonprofit
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide">
                    Messages
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide">
                    Status
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide hidden lg:table-cell">
                    Last Message
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide hidden lg:table-cell">
                    Date
                  </th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {inquiries.map((inq) => (
                  <tr
                    key={inq.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/dashboard/admin-inquiries/${inq.id}`)}
                  >
                    <td className="px-6 py-4">
                      <span className="font-popinsMedium text-gray-800 line-clamp-1">
                        {inq.subject}
                      </span>
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">
                      <div className="text-gray-700 font-popinsRegular line-clamp-1">
                        {inq.organizationName ||
                          [inq.senderFirstName, inq.senderLastName]
                            .filter(Boolean)
                            .join(" ") ||
                          "—"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full bg-themeColor text-white text-xs font-popinsMedium">
                        {inq.messageCount ?? 0}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-block text-xs font-popinsMedium px-2.5 py-0.5 rounded-full ${
                          STATUS_STYLES[inq.status] ?? "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {STATUS_LABELS[inq.status] ?? inq.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500 font-popinsRegular hidden lg:table-cell">
                      <span className="line-clamp-1 max-w-[200px]">
                        {inq.lastMessage || "—"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500 font-popinsRegular hidden lg:table-cell">
                      {formatDate(inq.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      <ChevronRight size={16} className="text-gray-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainAdminInquiries;
